/**
 * Cumple — Tipos compartidos del modulo billing (ADR-013, ADR-014).
 *
 * Plans (Starter/Pro/Enterprise/internal), AddOns, Subscriptions y la
 * resolucion de Entitlements efectivos (plan + addons activos).
 *
 * El motor de resolucion vive en apps/api/src/billing/entitlements/.
 * Aqui solo viven schemas Zod, tipos y constantes compartidas entre
 * apps/api (resolucion) y apps/web (UI de planes, paywall, usage meter).
 */
import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────
// Plans y features
// ─────────────────────────────────────────────────────────────────────

export const PLAN_CODES = ['internal', 'starter', 'pro', 'enterprise'] as const;
export type PlanCode = (typeof PLAN_CODES)[number];

export const FEATURE_FLAGS = [
  'api_access',
  'sso',
  'whitelabel',
  'vanity_domain',
  'priority_support',
] as const;
export type FeatureFlag = (typeof FEATURE_FLAGS)[number];

/**
 * Limits de un plan. `null` representa "ilimitado" (Enterprise / internal).
 * Cero significa explicitamente cero (no se usa hoy pero se admite).
 */
export const PlanLimitsSchema = z.object({
  maxTemplates: z.number().int().min(0).nullable(),
  maxSites: z.number().int().min(0).nullable(),
  maxUsers: z.number().int().min(0).nullable(),
});
export type PlanLimits = z.infer<typeof PlanLimitsSchema>;

export const PlanSchema = z.object({
  id: z.guid(),
  code: z.enum(PLAN_CODES),
  name: z.string(),
  monthlyPriceUsd: z.number().nullable(),
  yearlyPriceUsd: z.number().nullable(),
  limits: PlanLimitsSchema,
  features: z.array(z.enum(FEATURE_FLAGS)),
  isPublic: z.boolean(),
});
export type Plan = z.infer<typeof PlanSchema>;

// ─────────────────────────────────────────────────────────────────────
// AddOns
// ─────────────────────────────────────────────────────────────────────

export const ADDON_CODES = [
  'sso',
  'vanity_domain',
  'whitelabel',
  'priority_support',
  'content_pack',
  'extra_users',
  'extra_sites',
] as const;
export type AddOnCode = (typeof ADDON_CODES)[number];

/**
 * Delta sobre los limits del plan base. Se suma por addon × quantity.
 * Solo se suma si el plan tiene limit numerico (no se aplica si plan es
 * ilimitado).
 */
export const AddOnLimitsDeltaSchema = z.object({
  maxTemplates: z.number().int().optional(),
  maxSites: z.number().int().optional(),
  maxUsers: z.number().int().optional(),
});
export type AddOnLimitsDelta = z.infer<typeof AddOnLimitsDeltaSchema>;

export const AddOnSchema = z.object({
  id: z.guid(),
  code: z.enum(ADDON_CODES),
  name: z.string(),
  monthlyPriceUsd: z.number(),
  limitsDelta: AddOnLimitsDeltaSchema,
  featuresGrant: z.array(z.enum(FEATURE_FLAGS)),
});
export type AddOn = z.infer<typeof AddOnSchema>;

// ─────────────────────────────────────────────────────────────────────
// Subscription
// ─────────────────────────────────────────────────────────────────────

export const SUBSCRIPTION_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'suspended',
  'cancelled',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const CURRENCIES = ['USD', 'DOP'] as const;
export type Currency = (typeof CURRENCIES)[number];

export const BILLING_CYCLES = ['monthly', 'yearly', 'biennial'] as const;
export type BillingCycle = (typeof BILLING_CYCLES)[number];

export const PAYMENT_PROVIDERS = ['paypal', 'azul', 'cardnet', 'transfer', 'manual'] as const;
export type PaymentProvider = (typeof PAYMENT_PROVIDERS)[number];

export const PAYMENT_STATUSES = ['pending', 'paid', 'failed', 'refunded', 'disputed'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

// ─────────────────────────────────────────────────────────────────────
// Entitlements efectivos — resultado de resolver plan + addons activos
// ─────────────────────────────────────────────────────────────────────

/**
 * Entitlements activos de un tenant en este momento. El motor en
 * apps/api/src/billing/entitlements/ resuelve estos valores a partir
 * de subscription + addons activos, cacheados en Redis TTL 60s.
 *
 * `null` en un limit significa ilimitado. Para checar "alcanzado", siempre
 * usar el helper `isWithinLimit(current, max)` que trata null como
 * Infinity.
 */
export interface Entitlements {
  tenantId: string;
  planCode: PlanCode;
  status: SubscriptionStatus;
  limits: PlanLimits;
  features: ReadonlySet<FeatureFlag>;
  /** `true` si la subscription esta en `trialing` y trial_ends_at > now */
  isInTrial: boolean;
  /** `true` si esta en past_due (cobranza pendiente, gracia 14d antes de suspend) */
  isInGracePeriod: boolean;
  /** ISO datetime cuando expira el trial; null si no esta en trial */
  trialEndsAt: string | null;
  /** ISO datetime cuando se resolvio. Util para debugging cache stale. */
  computedAt: string;
}

/**
 * Resuelve entitlements efectivos combinando plan + addons activos.
 * Funcion pura (no toca DB): el caller pasa el plan + addons + status ya
 * cargados. Para resolver desde DB usar `resolveEntitlements()` en
 * apps/api/src/billing/entitlements/service.ts.
 */
export function computeEffectiveEntitlements(input: {
  tenantId: string;
  plan: Plan;
  status: SubscriptionStatus;
  activeAddOns: { addon: AddOn; quantity: number }[];
  trialEndsAt: Date | null;
}): Entitlements {
  const { tenantId, plan, status, activeAddOns, trialEndsAt } = input;

  // Limits efectivos: plan.limits + sum(addon.limitsDelta × qty)
  // Solo se suma si el limit del plan no es null (Enterprise ya es ilimitado).
  const limits: PlanLimits = {
    maxTemplates: plan.limits.maxTemplates,
    maxSites: plan.limits.maxSites,
    maxUsers: plan.limits.maxUsers,
  };

  for (const { addon, quantity } of activeAddOns) {
    if (limits.maxTemplates !== null && addon.limitsDelta.maxTemplates !== undefined) {
      limits.maxTemplates += addon.limitsDelta.maxTemplates * quantity;
    }
    if (limits.maxSites !== null && addon.limitsDelta.maxSites !== undefined) {
      limits.maxSites += addon.limitsDelta.maxSites * quantity;
    }
    if (limits.maxUsers !== null && addon.limitsDelta.maxUsers !== undefined) {
      limits.maxUsers += addon.limitsDelta.maxUsers * quantity;
    }
  }

  // Features efectivas: plan.features ∪ ⋃ addon.featuresGrant
  const features = new Set<FeatureFlag>(plan.features);
  for (const { addon } of activeAddOns) {
    for (const f of addon.featuresGrant) features.add(f);
  }

  const now = new Date();
  const isInTrial =
    status === 'trialing' && trialEndsAt !== null && trialEndsAt.getTime() > now.getTime();

  return {
    tenantId,
    planCode: plan.code,
    status,
    limits,
    features,
    isInTrial,
    isInGracePeriod: status === 'past_due',
    trialEndsAt: trialEndsAt ? trialEndsAt.toISOString() : null,
    computedAt: now.toISOString(),
  };
}

/**
 * Helper: verifica si current esta dentro de max. null/Infinity = OK siempre.
 */
export function isWithinLimit(current: number, max: number | null): boolean {
  if (max === null) return true;
  return current < max;
}

/**
 * Helper: verifica si current esta exactamente AL limit (para mostrar
 * banner "alcanzaste el limite" antes de bloquear el siguiente intento).
 */
export function isAtLimit(current: number, max: number | null): boolean {
  if (max === null) return false;
  return current >= max;
}

/**
 * Helper: porcentaje de uso (0-100) para barras de progreso.
 * Devuelve 0 si max es null (ilimitado).
 */
export function usagePercentage(current: number, max: number | null): number {
  if (max === null || max === 0) return 0;
  return Math.min(100, Math.round((current / max) * 100));
}
